import React from 'react';
import type { ComponentType, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import Helmet from 'react-helmet';
import Breadcrumbs from 'components/breadcrumbs/breadcrumbs';
import type { BreadcrumbSelector } from 'components/breadcrumbs/use-breadcrumbs';
import usePageTracking from 'hooks/use-page-tracking';
import ProjectArchivedAlert from 'components/projects/project-archived-alert';
import useIsMobile from 'hooks/use-is-mobile';

type InnerLayerProps = {
  title: string;
  breadcrumbs?: BreadcrumbSelector;
  container?: ComponentType<{ children?: ReactNode }>;
  children?: ReactNode;
};

function InnerLayout({
  title,
  breadcrumbs,
  container: Container = CenteredContentContainer,
  children
}: InnerLayerProps) {
  usePageTracking(title);
  const { isMobile } = useIsMobile();

  return (
    <InnerLayoutContainer data-e2e="inner-layout">
      <Helmet>
        <title>env0 - {title}</title>
      </Helmet>
      <ContentArea>
        {!isMobile && (
          <BreadcrumbsContainer>
            <Breadcrumbs selector={breadcrumbs} />
          </BreadcrumbsContainer>
        )}
        <Wrapper>
          <ProjectArchivedAlert />
          <Container>{children}</Container>
        </Wrapper>
      </ContentArea>
    </InnerLayoutContainer>
  );
}

export default InnerLayout;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  background-color: ${({ theme }) => theme.innerLayoutContentBackground};
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-top: 25px;
`;

export const CenteredContentContainer = styled(ContentContainer)`
  width: 90%;
  max-width: 1400px;
  margin: auto;
  background-color: ${({ theme }) => theme.innerLayoutContentBackground};
`;

export const FullSizeContentContainer = styled(ContentContainer)`
  padding-top: 0;
`;

const InnerLayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

/**
 * https://www.figma.com/design/vhfYlbrrUsQrx6AvWGcE9h/Design-system?node-id=225-2043
 *
 * Measured 12 / 20 / 12 / 24 on Navigation/Secondary/surface with a 1px border/primary bottom, 38px in
 * total. The height sits here rather than on the crumb, and centres it, so the strip stays 38px whatever
 * line-height the type ramp resolves to - which is what `navbar-header.tsx` is aligned against.
 */
const BreadcrumbsContainer = styled.div`
  flex-shrink: 0;

  ${legacyOnly(
    theme => css`
      background: ${theme.primaryWhite};
      border-bottom: 1px solid ${theme.separatorGray};
      padding: 0 20px;
    `
  )}

  ${newDesignOnly(
    theme => css`
      display: flex;
      align-items: center;
      height: ${theme.layout.breadcrumbBarHeight};
      background: var(--surface-main-page);
      border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);
      padding: 0 ${theme.padding.xl} 0 ${theme.padding.xxl};
    `
  )}
`;
